/**
 * Ephemeral location decoding and validation utilities.
 *
 * Used by PreviewMap to decode Base64-encoded location data from URL query parameters.
 */

export interface EphemeralLocation {
  id: string
  name: string
  lat: number
  lng: number
}

export interface DecodedLocation {
  locationId: string
  name: string
  coordinates: { latitude: number; longitude: number }
}

export interface EphemeralDecodeResult {
  locations?: DecodedLocation[]
  error?: string
}

const MAX_LOCATIONS_PARAM_LENGTH = 2000

/**
 * Decode and validate ephemeral location data from a Base64-encoded query parameter.
 * Returns either valid locations in the persistent-mode format, or an error string.
 */
export function decodeEphemeralLocations(locationsParam: string | null): EphemeralDecodeResult {
  if (!locationsParam) {
    return { error: 'No location data provided.' }
  }

  if (locationsParam.length > MAX_LOCATIONS_PARAM_LENGTH) {
    return { error: 'Location data too large for preview.' }
  }

  let decoded: string
  try {
    decoded = atob(locationsParam)
  } catch {
    return { error: 'Invalid location data.' }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(decoded)
  } catch {
    return { error: 'Invalid location data.' }
  }

  if (!Array.isArray(parsed)) {
    return { error: 'Invalid location data.' }
  }

  const validLocations = (parsed as any[]).filter((entry): entry is EphemeralLocation => {
    if (!entry || typeof entry !== 'object') return false
    if (typeof entry.id !== 'string' || !entry.id) return false
    if (typeof entry.name !== 'string' || !entry.name) return false
    if (typeof entry.lat !== 'number' || entry.lat < -90 || entry.lat > 90) return false
    if (typeof entry.lng !== 'number' || entry.lng < -180 || entry.lng > 180) return false
    return true
  })

  if (validLocations.length === 0) {
    return { error: 'No valid locations found in the provided data.' }
  }

  return {
    locations: validLocations.map(loc => ({
      locationId: loc.id,
      name: loc.name,
      coordinates: { latitude: loc.lat, longitude: loc.lng }
    }))
  }
}
